<template>
  <div data-testid="visitor-section">
    <VisitorProfilesHeader />

    <ContentCard class="mt-5 pb-6">
      <template #header>
        <VisitorProfilesTabs
          :selected-tab="selectedTab"
          :status-counts="visitorProfilesResult?.visitorProfiles.statusCounts"
          @update:selected-tab="handleTabUpdate"
        />
      </template>

      <VisitorProfilesFilterPanel
        :selected-filters="selectedFilters"
        :facets="visitorProfilesResult?.visitorProfiles.facets"
        @update:company-names="($filters) => updateFilters('companyNames', $filters)"
        @update:company-positions="($filters) => updateFilters('companyPositions', $filters)"
        @update:company-job-levels="($filters) => updateFilters('companyJobLevels', $filters)"
        @update:company-sizes="($filters) => updateFilters('companySizes', $filters)"
        @update:company-fields="($filters) => updateFilters('companyFields', $filters)"
        @update:company-departments="($filters) => updateFilters('companyDepartments', $filters)"
        @update:company-industries="($filters) => updateFilters('companyIndustries', $filters)"
        @update:company-types="($filters) => updateFilters('companyTypes', $filters)"
        @update:countries="($filters) => updateFilters('countries', $filters)"
        @update:user-interests="($filters) => updateFilters('userInterests', $filters)"
        @update:networking-interests="($filters) => updateFilters('networkingInterests', $filters)"
        @reset="resetFilters"
      />

      <VisitorProfilesList
        :visitors="visitorProfilesResult?.visitorProfiles.visitorProfiles.nodes || []"
        :loading="visitorProfilesLoading"
        :status="status"
        :selected-filters="selectedFilters"
      />

      <PaginationControl
        v-if="visitorProfilesResult?.visitorProfiles.visitorProfiles.pageInfo"
        class="mt-6"
        :current-page="currentPage"
        :per-page="perPage"
        :total="visitorProfilesResult.visitorProfiles.visitorProfiles.pageInfo.totalCount"
        @update:current-page="updateCurrentPage"
        @update:per-page="updatePerPage"
      />
    </ContentCard>
  </div>
</template>

<script setup lang="ts">
import { assertUnreachable } from '@ramp106/omrjs-common'
import { PaginationControl } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'

import type {
  CompanyDepartmentEnum,
  CompanyTypeEnum,
  CompanySizeEnum,
  JobLevelEnum,
  VisitorProfilesFiltersInput,
  VisitorProfilesQueryVariables,
} from '@/gql/myomr'
import { useVisitorProfilesQuery, VisitorProfileStatusEnum } from '@/gql/myomr'
import { showNotification } from '@/helpers/notificationHelper'

import { ROOT_EVENT_SLUG } from './config'
import { useVisitorProfilesRouter } from './useVisitorProfilesRouter'
import VisitorProfilesFilterPanel from './VisitorProfilesFilterPanel.vue'
import VisitorProfilesHeader from './VisitorProfilesHeader.vue'
import VisitorProfilesList from './VisitorProfilesList.vue'
import VisitorProfilesTabs from './VisitorProfilesTabs.vue'

const DEFAULT_PER_PAGE = 12

const {
  selectedTab,
  currentPage,
  perPage,
  selectedFilters,
  handleTabUpdate,
  updateCurrentPage,
  updatePerPage,
  updateFilters,
  resetFilters,
} = useVisitorProfilesRouter(DEFAULT_PER_PAGE)

const status = computed(() => {
  let status: VisitorProfileStatusEnum | undefined

  switch (selectedTab.value) {
    case 'all':
      status = undefined
      break
    case 'new':
      status = VisitorProfileStatusEnum.Unprocessed
      break
    case 'interesting':
      status = VisitorProfileStatusEnum.Interesting
      break
    case 'contacted':
      status = VisitorProfileStatusEnum.Contacted
      break
    case 'not_relevant':
      status = VisitorProfileStatusEnum.NotRelevant
      break
    default:
      assertUnreachable(selectedTab.value)
  }

  return status
})

const variables = computed(() => {
  const filters: VisitorProfilesFiltersInput = {
    eventSlug: ROOT_EVENT_SLUG,
    companyNames: selectedFilters.value.companyNames,
    companyPositions: selectedFilters.value.companyPositions,
    companyJobLevels: selectedFilters.value.companyJobLevels.length
      ? (selectedFilters.value.companyJobLevels as JobLevelEnum[])
      : undefined,
    companySizes: selectedFilters.value.companySizes.length ? (selectedFilters.value.companySizes as CompanySizeEnum[]) : undefined,
    companyFieldIds: selectedFilters.value.companyFields.length ? selectedFilters.value.companyFields : undefined,
    companyDepartments: selectedFilters.value.companyDepartments.length
      ? (selectedFilters.value.companyDepartments as CompanyDepartmentEnum[])
      : undefined,
    companyIndustryIds: selectedFilters.value.companyIndustries.length ? selectedFilters.value.companyIndustries : undefined,
    companyTypes: selectedFilters.value.companyTypes.length ? (selectedFilters.value.companyTypes as CompanyTypeEnum[]) : undefined,
    countries: selectedFilters.value.countries.length ? selectedFilters.value.countries : undefined,
    userInterestIds: selectedFilters.value.userInterests.length ? selectedFilters.value.userInterests.map(Number) : undefined,
    networkingInterestIds: selectedFilters.value.networkingInterests.length
      ? selectedFilters.value.networkingInterests.map(Number)
      : undefined,
    status: status.value,
  }

  const vars: VisitorProfilesQueryVariables = {
    limit: perPage.value,
    offset: (currentPage.value - 1) * perPage.value,
    filters,
  }

  return vars
})

const {
  result: visitorProfilesResult,
  loading: visitorProfilesLoading,
  onError,
} = useVisitorProfilesQuery(variables, { fetchPolicy: 'network-only' })

onError((error) => {
  showNotification(error.message, 'error')
})
</script>
